import { useEffect, memo, useMemo } from 'react';
import { useMedia } from 'react-use';
import { setByTextValue } from 'redux-implementations/textSearch';

import { screen } from 'components/styles/variables';
import useHashParamFeatureFlag from 'lib/hooks/useHashParamFeatureFlag';
import Dropdown from './Dropdown';
import { styles } from './styles';
import useTextSearch, { shouldComponentUpdate, useOptimizedClickAway } from './utils';
import InputField from './InputField';

export interface TextSearchProps {
  toggleModal?: () => void;
  setInputText?: (text: string) => void;
  onClick?: () => void;
  onChange?: (item: TextSearchValue) => void;
  value?: TextSearchValue;
}

export type TextSearchValue = {
  make?: string;
  model?: string;
  bodyType?: string;
  searchText?: string;
};

const TextSearch = memo(
  function TextSearch({
    toggleModal,
    setInputText,
    onClick,
    onChange,
    value,
  }: TextSearchProps): JSX.Element {
    const isMobile = useMedia(`(max-width: ${screen.xsMax})`);

    const searchText = value?.searchText;
    const {
      state,
      handleInputChange,
      handleFocus,
      handleClickOutside,
      onDropdownClick,
      dispatch,
      handleInputKeyDown,
    } = useTextSearch(toggleModal, setInputText, onChange);
    const multiMakeModelEnabled = useHashParamFeatureFlag('multiMakeModel');

    useEffect(() => {
      if (!multiMakeModelEnabled) {
        dispatch(setByTextValue(searchText || ''));
      }
    }, [searchText, dispatch, multiMakeModelEnabled]);

    const textInputRef = useOptimizedClickAway<HTMLDivElement>(handleClickOutside);

    const inputValue = useMemo(() => state.textInput, [state]);

    return (
      <div className="search-container" ref={textInputRef}>
        <div className="dropdown-container">
          <Dropdown
            hide={!state.dropDownOpen}
            className={isMobile ? 'modal-dropdown' : ''}
            listItems={state.list}
            queryText={state.textInput}
            cursor={state.cursor}
            onClick={onDropdownClick}
          />
        </div>
        <div className="header-container">
          <InputField
            inputValue={inputValue}
            handleInputChange={handleInputChange}
            handleFocus={handleFocus}
            handleInputKeyDown={handleInputKeyDown}
            onClick={onClick}
          />
          {isMobile && <button className="close-button" onClick={toggleModal} />}
        </div>
        {isMobile && !state.textInput && <div className="spacer" />}
        <style jsx>{styles}</style>
      </div>
    );
  },
  (prevProps, nextProps) => shouldComponentUpdate(prevProps, nextProps)
);

export default TextSearch;
